// External imports
import React, { useEffect, useState } from 'react';
import { Button, Typography, Paper, Modal, withStyles } from '@material-ui/core';
import downloadIcon from '~/images/icons/downloadIcon.svg';
// Styling imports
import { styles } from '~/app/Pages/Returns/components/Document/ArchivedDocuments/css/archivedDocuments.js';

// Archived documents can come from outside sources, so we cannot trust the
// uploaded content type. Rendering HTML or SVG inline could allow scripts to run
// in the user's authenticated session. Only PDFs and raster images are rendered
// inline. All other file types are download-only.
const isPreviewableType = type =>
  !!type && type !== 'image/svg+xml' && (type === 'application/pdf' || type.startsWith('image/'));

const PreviewDocumentModal = props => {
  const { classes } = props;
  const [url, setUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);

  useEffect(() => {
    if (!props.currentOpenAttachment) return;

    // Large documents are never previewed inline (see render) — skip the
    // expensive base64 decode and only resolve the content type so the
    // download-only / "too large" messaging still renders correctly.
    if (isDocumentLarge()) {
      setMimeType(resolveMimeType(props.currentOpenAttachment));
      return;
    }

    // Call the helper function to generate the Blob URL
    const result = buildBlobUrl(props.currentOpenAttachment);

    if (result) {
      setUrl(result.url);
      setMimeType(result.mimeType);

      // Cleanup when component unmounts or attachment changes
      if (result.url) {
        return () => URL.revokeObjectURL(result.url);
      }
    }
  }, [props.currentOpenAttachment]);

  // Parse the content type declared in the data-URI prefix without decoding
  // the (potentially large) base64 payload.
  const resolveMimeType = base64Raw => {
    const divisionIndex = base64Raw.lastIndexOf(',');
    const prefix = divisionIndex !== -1 ? base64Raw.slice(0, divisionIndex) : '';
    const mimeMatch = prefix.match(/^data:([^;]+)/);
    return mimeMatch ? mimeMatch[1] : 'application/pdf';
  };

  // Convert a base64 (data URI) document to a Blob URL, preserving the original
  // content type. The backend returns documents as "data:<mime>;base64,<data>"
  // — images stay images, PDFs stay PDFs — so the blob must carry the real MIME
  // type or the browser fails to render non-PDF attachments. A blob URL is only
  // produced for previewable types; other types return a null url (the MIME
  // type is still reported so the UI can show a download-only message).
  const buildBlobUrl = base64Raw => {
    if (!base64Raw) return null;

    const resolvedMimeType = resolveMimeType(base64Raw);

    // Do not build a navigable blob URL for non-previewable types — see the
    // note on isPreviewableType above.
    if (!isPreviewableType(resolvedMimeType)) {
      return { url: null, mimeType: resolvedMimeType };
    }

    // Split off the data URI prefix to get the raw base64 payload
    const divisionIndex = base64Raw.lastIndexOf(',');
    const cleanBase64 = divisionIndex !== -1 ? base64Raw.slice(divisionIndex + 1) : base64Raw;

    try {
      // Decode base64 to binary string
      const byteCharacters = atob(cleanBase64);

      // Convert binary string to byte array
      const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);

      // Create the Blob using the document's actual content type
      const blob = new Blob([byteArray], { type: resolvedMimeType });

      // Generate temporary object URL
      return { url: URL.createObjectURL(blob), mimeType: resolvedMimeType };
    } catch (err) {
      console.error('Document processing failed:', err);
      return { url: null, mimeType: resolvedMimeType }; // url null if decoding fails
    }
  };

  // check if document is over 1.5 mb
  const isDocumentLarge = () => {
    // forumla to get approximate size (in bytes) of base64 String
    // n * (3/4) where n is the length base64 String
    const fileSize = props.currentOpenAttachment?.length * 0.75;

    // 1.5 mb = 1500000 bytes
    if (fileSize > 1500000) {
      return true;
    }
    return false;
  };

  const openDocumentWindow = () => {
    const result = buildBlobUrl(props.currentOpenAttachment);

    // result.url is null for non-previewable types — never window.open those.
    if (result.url) {
      window.open(result.url);

      // Cleanup after a delay to allow the new tab to load
      setTimeout(() => {
        URL.revokeObjectURL(result.url);
      }, 10000);
    }
  };

  return (
    <Modal
      id="mdlAddArchiveDocumentModalDocArchive"
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      open={props.isPreviewModalOpen}
      className={classes.modalSpacing}
      onClose={() => props.toggleModal('isPreviewModalOpen')}
    >
      <Paper className={classes.modal}>
        <Typography variant="body1" className={classes.modalTitle}>
          Preview {props.currentOpenAttachmentTitle}
        </Typography>
        <div className="preview-doc-archive-modal-container">
          <table>
            <tbody>
              <tr>
                <td className={classes.modalDesc}>
                  Description : {props.currentOpenAttachmentDesc}
                </td>
                <td className={classes.modalDocLink}>
                  <a
                    className="preview-doc-archive-modal-container-table-td-a"
                    onClick={() => props.toggleModal('isRequestPDFModalOpen')}
                  >
                    <img
                      className="preview-doc-archive-modal-container-table-td-a-img"
                      src={downloadIcon}
                    />
                    <button
                      id="mdlDocumentArchiveDownloadDocBtn"
                      className="preview-doc-archive-modal-container-table-td-a-btn"
                    >
                      Download Document
                    </button>
                  </a>
                </td>
                <td>
                  <Button
                    id="mdlDocumentArchiveDownloadDocCloseBtn"
                    variant="contained"
                    className={classes.previewModalBtn}
                    color="primary"
                    onClick={() => props.toggleModal('isPreviewModalOpen')}
                  >
                    Close
                  </Button>
                </td>
              </tr>
              <tr className={classes.modalSecondaryData}>
                <td>Uploaded : {props.currentOpenAttachmentDateUp}</td>
              </tr>
              <tr>
                <td className={classes.modalSecondaryDataTwo}>
                  Type : {props.currentOpenAttachmentType}
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <table>
              <tbody>
                <tr>
                  <td>
                    {props?.currentOpenAttachment &&
                      (mimeType && !isPreviewableType(mimeType) ? (
                        <div className="preview-doc-archive-modal-container-table-body">
                          This document type cannot be previewed. Please use Download Document above
                          to save the file.
                        </div>
                      ) : isDocumentLarge() ? (
                        <div className="preview-doc-archive-modal-container-table-body">
                          Document is too large to preview. Please open in a new&nbsp;
                          <a href="javascript:;" onClick={openDocumentWindow}>
                            window
                          </a>
                        </div>
                      ) : url ? (
                        mimeType?.startsWith('image/') ? (
                          <img
                            src={url}
                            alt="Document Preview"
                            style={{ maxWidth: '950px', maxHeight: '650px' }}
                          />
                        ) : (
                          <iframe width="950" height="650" src={url} title="Document Preview" />
                        )
                      ) : (
                        <div className="preview-doc-archive-modal-container-table-body">
                          Unable to Preview. Please open in a new&nbsp;
                          <a href="javascript:;" onClick={openDocumentWindow}>
                            window
                          </a>
                        </div>
                      ))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Paper>
    </Modal>
  );
};

export default withStyles(styles)(PreviewDocumentModal);
